import { createTheme } from "@mui/material/styles";

// Extend the theme to include custom colors and design tokens
declare module '@mui/material/styles' {
  interface Palette {
    table: {
      header: string;
      headerText: string;
      userHeader: string;
      rowOdd: string;
      rowEven: string;
      footer: string;
      footerText: string;
      separator: string;
      totalHighlight: string;
    };
    custom: {
      darkTeal: string;
      lightGray: string;
      mediumGray: string;
      sidebarIcon: string;
      sidebarIconActive: string;
    };
    status: {
      overtime: {
        main: string;
        light: string;
        circle: string;
      };
      sufficient: {
        main: string;
        light: string;
        circle: string;
      };
      insufficient: {
        main: string;
        light: string;
        circle: string;
      };
      upcoming: {
        main: string;
        text: string;
        circle: string;
      };
    };
    holiday: {
      national: string;
      pending: string;
      approved: string;
      sickLeave: string;
      rejected: string;
      icon: string;
      iconBg: string;
      circularProgress: string;
    };
    homeOffice: {
      icon: string;
      iconBg: string;
    };
  }
  interface PaletteOptions {
    table?: {
      header?: string;
      headerText?: string;
      userHeader?: string;
      rowOdd?: string;
      rowEven?: string;
      footer?: string;
      footerText?: string;
      separator?: string;
      totalHighlight?: string;
    };
    custom?: {
      darkTeal?: string;
      lightGray?: string;
      mediumGray?: string;
      sidebarIcon?: string;
      sidebarIconActive?: string;
    };
    status?: {
      overtime?: {
        main?: string;
        light?: string;
        circle?: string;
      };
      sufficient?: {
        main?: string;
        light?: string;
        circle?: string;
      };
      insufficient?: {
        main?: string;
        light?: string;
        circle?: string;
      };
      upcoming?: {
        main?: string;
        text?: string;
        circle?: string;
      };
    };
    holiday?: {
      national?: string;
      pending?: string;
      approved?: string;
      sickLeave?: string;
      rejected?: string;
      icon?: string;
      iconBg?: string;
      circularProgress?: string;
    };
    homeOffice?: {
      icon?: string;
      iconBg?: string;
    };
  }

  interface TypographyVariants {
    reportTitle: React.CSSProperties;
    reportSubtitle: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    reportTitle?: React.CSSProperties;
    reportSubtitle?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    reportTitle: true;
    reportSubtitle: true;
  }
}

const theme = createTheme({
    palette: {
        primary: {
            main: "#3B3B3B",
            light: "#626262",
            dark: "#292929",
            contrastText: "#e8eced",
        },

        secondary: {
            main: "#3F9142",
            light: "#65A767",
            dark: "#2C652E",
        },
        error: {
            main: "#D32F2F",
        },
        warning: {
            main: "#FF9800",
        },
        background: {
            default: "#FCFFFD",
            paper: "#e8eced",
        },
        // Custom table colors - centralized for reuse
        table: {
            header: "#102e3b",        // Dark teal/blue for headers
            headerText: "#ffffff",    // White text on headers
            userHeader: "#3a7ca5",    // Medium blue for user name rows
            rowOdd: "#ffffff",        // White for odd rows
            rowEven: "#f9fafc",       // Light gray for even rows
            footer: "#102e3b",        // Same as header for footer
            footerText: "#ffffff",    // White text on footer
            separator: "#000000",     // Black for separator lines
            totalHighlight: "#f0a868", // Warm orange highlight for total hours cells
        },
        // Custom colors for reuse across components
        custom: {
            darkTeal: "#102e3b",      // Primary dark teal/blue (headers, bars, sidebar)
            lightGray: "#e8eced",     // Light gray (backgrounds, inactive tabs)
            mediumGray: "#ccc",       // Medium gray (secondary text)
            sidebarIcon: "#8a9397",   // Sidebar icon color (inactive)
            sidebarIconActive: "#e8eced", // Sidebar icon color (active)
        },
        // Status colors for time tracking
        status: {
            overtime: {
                main: "#f59393",      // Light red background
                light: "red",         // Red text
                circle: "#f57a7a",    // Circle indicator
            },
            sufficient: {
                main: "#dff3de",      // Light green background
                light: "#6fc32f",     // Green text
                circle: "#b8e6b3",    // Circle indicator
            },
            insufficient: {
                main: "#feebaa",      // Light yellow background
                light: "#fdb15e",     // Orange text
                circle: "#fdcc5e",    // Circle indicator
            },
            upcoming: {
                main: "#e0e0e0",      // Gray background
                text: "black",        // Black text
                circle: "lightgray",  // Light gray circle
            },
        },
        // Holiday colors
        holiday: {
            national: "#feebaa",      // National holiday (yellow)
            pending: "#b6d9fd",       // Pending approval (blue)
            approved: "#ddf5d7",      // Approved regular leave (green)
            sickLeave: "#CE93D8",     // Approved sick leave (purple)
            rejected: "#D32F2F",      // Rejected (red - uses error.main)
            icon: "#f7c232",          // Holiday icon color
            iconBg: "#feedc2",        // Holiday icon background
            circularProgress: "#338ccd", // Circular progress indicator color
        },
        // Home office colors
        homeOffice: {
            icon: "#509452",          // Home office icon color
            iconBg: "#91c993",        // Home office icon background
        },
    },
    typography: {
        allVariants: {
            color: "#3B3B3B",
        },
        // Custom typography variants for reports
        reportTitle: {
            fontSize: '1.2rem',
            fontWeight: 500,
        },
        reportSubtitle: {
            fontSize: '1rem',
            // Color will be inherited from custom.mediumGray via component styling
        },
    },
    spacing: 8, // Base spacing unit (8px)
});

export default theme;
